import { ChartTooltip } from './ChartTooltip';
import type { BandScale } from './scale';
import type { Formatter, Series } from './types';
import type { ChartCursor } from './useChartCursor';

interface CursorLayerProps {
  cursor: ChartCursor;
  series: readonly Series[];
  format: Formatter;
  x: BandScale;
  width: number;
}

/**
 * The focusable cursor, its tooltip and the live region that speaks for it.
 *
 * Plain HTML positioned over the plot, passed to `ChartFrame` as its `overlay`
 * render prop. Not a `foreignObject`: those clip unpredictably in Safari, drop
 * pointer events near their edges, and cannot let a tooltip overflow the plot
 * to stay on screen.
 *
 * Shared by every chart with a categorical axis, so the keyboard contract is
 * written once. A chart type that re-implemented this would eventually
 * re-implement it slightly differently, and "the arrow keys work on the bars
 * but not the lines" is the kind of thing nobody reports and everybody notices.
 */
export function CursorLayer({ cursor, series, format, x, width }: CursorLayerProps) {
  return (
    <div className="c-chart__cursor-layer">
      {/*
       * One focusable element for the whole chart, not one per point. Sixty
       * points would be sixty tab stops, and a page of four charts would be
       * two hundred and forty — at which point nobody tabs through it, they
       * tab past it, and the chart is not keyboard-accessible after all.
       */}
      <div
        className="c-chart__cursor"
        tabIndex={0}
        role="group"
        aria-label="Chart values. Use the left and right arrow keys to read each one."
        onKeyDown={cursor.onKeyDown}
        onFocus={cursor.onFocus}
        onBlur={cursor.onBlur}
      />

      <ChartTooltip
        activeKey={cursor.key}
        series={series}
        format={format}
        x={cursor.key === undefined ? 0 : x.centre(cursor.key)}
        width={width}
      />

      {/*
       * Polite, and fed only by keyboard movement — see `cursorAnnouncement`.
       * A pointer user reading a tooltip does not want their screen reader
       * narrating every pixel of travel, and a keyboard user must not be told
       * each value twice.
       */}
      <p className="sr-only" role="status">
        {cursorAnnouncement(cursor, series, format)}
      </p>
    </div>
  );
}

/**
 * What the live region says for the current cursor position.
 *
 * Empty unless the cursor was moved by a key. Built here rather than in the
 * hook because it needs the formatter and the series labels, which are a
 * rendering concern.
 */
function cursorAnnouncement(
  cursor: ChartCursor,
  series: readonly Series[],
  format: Formatter,
): string {
  if (cursor.mode !== 'keyboard' || cursor.key === undefined) return '';

  const rows = series
    .map((one) => ({ one, point: one.points.find((candidate) => candidate.key === cursor.key) }))
    .filter((row) => row.point !== undefined);

  const heading = rows[0]?.point?.label ?? cursor.key;
  const readings = rows
    .map(({ one, point }) =>
      // Named, not printed as a zero — the same rule the tooltip follows.
      point?.value == null ? `${one.label} no data` : `${one.label} ${format(point.value)}`,
    )
    .join(', ');

  return `${heading}: ${readings}`;
}
